import {mkEl} from '../ts-lib/functions';

export interface ButtonI {
    title: string,
    cb?: (params?: any) => void,
    type?: string,
    classList?: string | string []
}

export interface ModalParams {
    width?: string,
    height?: string,
    margin?: string,
    display?: string,
    zIndex?: string,
    class?: string[],
    confirm?: ButtonI,
    cancel?: ButtonI,
}

export class Modal {
    dynamic = false;
    private modal: HTMLElement;
    private modalContent: Element;
    private readonly closeBtn: Element;
    private title: Element;
    private content: Element;
    private contentContainer: Element;
    private readonly actionsContainer: Element;

    private confirmBtn: HTMLElement = null;
    private confirmCallback: (params: any) => void;

    private cancelBtn: HTMLElement = null;
    private cancelCallback: (params: any) => void;

    private readonly closeModal: () => void;

    private readonly defaultParams: ModalParams = {
        width: '50%',
        height: 'auto',
        display: 'table',
        confirm: {
            title: 'Ok',
            cb: null
        },
    }

    constructor(selector?: string) {
        let modal = document.getElementById(selector);

        if (!modal) {
            modal = this.createModal(selector);
            this.dynamic = true;
        }

        const that = this;

        if (modal) {
            this.modal = modal;

            this.closeBtn = modal.querySelectorAll('.close')[0];
            this.title = modal.querySelectorAll('.sta-modal-title')[0];
            this.content = modal.querySelectorAll('.modal-text')[0];
            this.modalContent = modal.querySelectorAll('.sta-modal-content')[0];
            this.contentContainer = modal.querySelectorAll('.modal-container')[0];

            if (modal.querySelectorAll('.modal-actions')) {
                this.actionsContainer = modal.querySelectorAll('.modal-actions')[0];
            }

            this.closeModal = () => {
                this.title.innerHTML = '';
                this.content.innerHTML = '';
                this.contentContainer.innerHTML = '';
                this.actionsContainer.innerHTML = '';
                this.modal.style.display = "none";

                if (this.dynamic) {
                    this.modal.remove();
                }
            }

            if (this.closeBtn) {
                this.closeBtn.addEventListener('click', () => {
                    this.closeModal();
                });
            }

            window.onclick = function (event: Event) {
                if (event.target == modal) {
                    that.closeModal();
                }
            }
        }
    }

    open(title: string, text: string, content: HTMLElement, opt: ModalParams = null) {
        this.title.innerHTML = title;
        this.content.innerHTML = text || '';
        this.contentContainer.innerHTML = '';
        this.contentContainer.appendChild(content);
        this.modal.style.display = opt.display || this.defaultParams.display;
        this.applyParams(opt)
    }

    applyParams(opt: ModalParams = null) {
        if (opt?.width) {
            (this.modalContent as HTMLElement).style.width = opt.width;
        }

        if (opt?.height) {
            (this.modalContent as HTMLElement).style.height = opt.height;
        }

        if (opt?.margin) {
            (this.modalContent as HTMLElement).style.margin = opt.margin;
        }

        if (opt?.zIndex) {
            this.modal.style.zIndex = opt.zIndex;
        }

        if (opt?.class) {
            this.modal.classList.add(...opt.class);
        }

        if (this.actionsContainer) {
            if (opt.confirm) {
                this.confirmBtn = this.createButton(opt.confirm);
                this.actionsContainer.appendChild(this.confirmBtn);
                this.setConfirmCallback(opt.confirm.cb)
            }

            if (opt.cancel) {
                this.cancelBtn = this.createButton(opt.cancel);
                this.actionsContainer.appendChild(this.cancelBtn);
                this.setCancelCallback(opt.cancel.cb)
            }
        }
    }


    alert(title: string, content: string, opt: ModalParams = null) {
        this.title.innerHTML = title;
        this.content.innerHTML = content;
        this.modal.style.display = this.defaultParams.display;

        if (!opt) {
            opt = this.defaultParams;
        }

        this.applyParams(opt)
    }

    public close() {
        this.closeModal();
    }

    async confirm(confirm: HTMLElement, cb: () => any): Promise<any> {
        console.info("Deprecated method: Modal.confirm");
        return new Promise(function (resolve) {
            confirm.addEventListener("click", function modalClose() {
                confirm.removeEventListener("click", modalClose);
                //call any handler you want here, if needed
                let val = cb();
                resolve(val);
            });
        });
    }

    async afterConfirm(): Promise<any> {
        if (this.confirmBtn) {
            let confirm = this.confirmBtn;
            let confirmCb = this.confirmCallback ? this.confirmCallback : null;
            return new Promise(function (resolve) {
                confirm.addEventListener("click", function modalClose() {
                    confirm.removeEventListener("click", modalClose);
                    //call any handler you want here, if needed
                    let val = null;
                    if (confirmCb) {
                        val = confirmCb(null);
                    }
                    resolve(val);
                });
            });
        } else {
            return null;
        }
    }

    createButton(opt: ButtonI): HTMLElement {
        let classes = ['button', 'primary', 'modal'];

        if (opt.classList) {
            if (typeof opt.classList === 'string') {
                classes.push(opt.classList)
            } else {
                classes = classes.concat(opt.classList)
            }
        }

        let btn = mkEl('input', {class: classes, value: opt.title})

        if (!opt.cb) {
            let cb = this.closeModal;
            btn.addEventListener("click", function anonymousFunction() {
                btn.removeEventListener("click", anonymousFunction);
                cb();
            });
        } else if (opt.cb && opt?.type === 'custom') {
            btn.addEventListener("click", function anonymousFunction() {
                btn.removeEventListener("click", anonymousFunction);
                opt.cb();
            });
        }

        return btn;
    }

    createModal(id: string = '') {
        let modal = mkEl('div', {class: ['sta-modal'], id: id});
        let middle = mkEl('div', {class: ['sta-modal-middle']});
        let content = mkEl('div', {class: ['sta-modal-content']});
        let header = mkEl('div', {class: ['sta-modal-header']});
        header.appendChild(mkEl('div', {class: ['close'], id: 'modal-close', html: '&#10006;'}));
        header.appendChild(mkEl('h2', {class: ['sta-modal-title']}));
        header.appendChild(mkEl('p', {class: ['modal-text']}));
        content.appendChild(header);
        content.appendChild(mkEl('div', {class: ['modal-container']}));
        content.appendChild(mkEl('div', {class: ['modal-actions']}));
        middle.appendChild(content);
        modal.appendChild(middle);
        document.body.appendChild(modal);
        return modal;
    }

    setConfirmCallback(cb: any) {
        if (cb) {
            this.confirmCallback = cb;
        } else {
            this.confirmCallback = this.closeModal;
        }
    }

    setCancelCallback(cb: any) {
        if (cb) {
            this.cancelCallback = cb;
        } else {
            this.cancelCallback = this.closeModal;
        }
    }

    getValue(type: string, name: string) {
        let inp = this.modal.querySelectorAll(`${type}[name="${name}"]`);
        let value = '';
        if (inp && inp[0]) {
            value = (inp[0] as HTMLInputElement).value;
        }

        return value;
    }
}



